import React, { useState, useEffect } from 'react';
import { Row, Col, DatePicker, Dropdown, Menu, Button, Table, Input, Space } from 'antd';
import axios from 'axios';
import './ContractSummary.scss';
import AdminMainLayout from '../../AdminComponents/AdminMainLayout/AdminMainLayout';
import DottedLoader from '../../DottedLoader/DottedLoader';
import moment from 'moment';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import ENVIRONMENT from '../../../../environments';
import { saveAs } from 'file-saver';

const { RangePicker } = DatePicker;
const DATE_FORMAT = 'YYYY-MM';
const BASE_DATA_MAP_KEYS = [
    'Cumulative Value',
    'Product models',
    'AR models',
    'Variants',
    'Upload existing models',
    'Bedding',
    'Lifestyle images (4K)',
    'Lifestyle images (1K)',
    'Silo images (4K)',
    'Silo Products',
    'Designed scenes',
    '360 spins'
];

const ContractSummary = () => {
    const [loading, setLoading] = useState(true);
    const [contractSummary, setContractSummary] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(moment().startOf('month').format(DATE_FORMAT));
    const [selectedDateRange, setSelectedDateRange] = useState({
        start: moment().startOf('month').format(DATE_FORMAT),
        end: moment().startOf('month').format(DATE_FORMAT)
    });
    const [tableData, setTableData] = useState([]);
    const [selectedBaseDataMapKey, setSelectedBaseDataMapKey] = useState('Cumulative Value');
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    let searchInput = null;

    const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
        return amount.toLocaleString(locale, {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
    
    // Helper to format non-cumulative quantities
    const formatQuantity = (value) => {
        return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }
    
    useEffect(() => {
        axios.get(ENVIRONMENT.CONTRACT_SUMMARIES, {
            transformResponse: res => res, // prevent Axios from auto-parsing
            responseType: 'text' // keep raw response so headers stay intact
        }).then(response => {
            const lastModified = response.headers['last-modified'];
            const jsonData = JSON.parse(response.data);
            setContractSummary(jsonData);
            console.log('Last Updated:', lastModified);
        });
    }, []);

    useEffect(() => {
        let data = []
        if (contractSummary) {
            Object.keys(contractSummary).map(company_id => {
                let currentCompanyData = contractSummary[company_id];
                let monthlyUsageValue = 0;
                let monthlyContractedValue = 0;
                let rangeUsageValue = 0;
                let rangeContractedValue = 0;

                if (selectedBaseDataMapKey === 'Cumulative Value') {
                    currentCompanyData['services_quota'].map(service_data => {
                        monthlyUsageValue += service_data['unit_price'] * (service_data['usage'][selectedMonth] || 0);
                        monthlyContractedValue += service_data['unit_price'] * (service_data['interval_quota'][selectedMonth] || 0);

                        if (selectedDateRange) {
                            const rangeValues = calculateValuesForRange(
                                service_data,
                                selectedDateRange.start,
                                selectedDateRange.end
                            );
                            rangeUsageValue += rangeValues.usage;
                            rangeContractedValue += rangeValues.contracted;
                        }
                    });
                } else {
                    const serviceData = currentCompanyData['services_quota'].find(
                        service => service.service_name === selectedBaseDataMapKey
                    );
                    
                    if (serviceData) {
                        monthlyUsageValue = serviceData['usage'][selectedMonth] || 0;
                        monthlyContractedValue = serviceData['interval_quota'][selectedMonth] || 0;

                        if (selectedDateRange) {
                            const rangeValues = calculateValuesForRange(
                                serviceData,
                                selectedDateRange.start,
                                selectedDateRange.end,
                                true
                            );
                            rangeUsageValue = rangeValues.usage;
                            rangeContractedValue = rangeValues.contracted;
                        }
                    }
                }

                const monthlyDelta = monthlyContractedValue - monthlyUsageValue;
                const monthlyDeltaPercent = monthlyContractedValue === 0 ? 0 : ((monthlyDelta / monthlyContractedValue) * 100).toFixed(0);
                
                const rangeDelta = rangeContractedValue - rangeUsageValue;
                const rangeDeltaPercent = rangeContractedValue === 0 ? 0 : ((rangeDelta / rangeContractedValue) * 100).toFixed(0);

                data.push({
                    key: company_id,
                    company_id: company_id,
                    company: currentCompanyData['company_display_name'],
                    monthlyContracted: selectedBaseDataMapKey === 'Cumulative Value' ? `$${formatCurrency(monthlyContractedValue)}` : formatQuantity(monthlyContractedValue),
                    monthlyUsage: selectedBaseDataMapKey === 'Cumulative Value' ? `$${formatCurrency(monthlyUsageValue)}` : formatQuantity(monthlyUsageValue),
                    monthlyDelta: selectedBaseDataMapKey === 'Cumulative Value' 
                        ? `$${monthlyDelta < 0 ? `(${formatCurrency(Math.abs(monthlyDelta))})` : formatCurrency(monthlyDelta)}` 
                        : monthlyDelta < 0 ? `(${Math.abs(monthlyDelta).toFixed(2)})` : monthlyDelta.toFixed(2),
                    monthlyDeltaPercent: `${monthlyDeltaPercent}%`,
                    monthlyDeltaValue: monthlyDelta, // for sorting
                    rangeContracted: selectedBaseDataMapKey === 'Cumulative Value' ? `$${formatCurrency(rangeContractedValue)}` : formatQuantity(rangeContractedValue),
                    rangeUsage: selectedBaseDataMapKey === 'Cumulative Value' ? `$${formatCurrency(rangeUsageValue)}` : formatQuantity(rangeUsageValue),
                    rangeDelta: selectedBaseDataMapKey === 'Cumulative Value'
                        ? `$${rangeDelta < 0 ? `(${formatCurrency(Math.abs(rangeDelta))})` : formatCurrency(rangeDelta)}`
                        : rangeDelta < 0 ? `(${Math.abs(rangeDelta).toFixed(2)})` : rangeDelta.toFixed(2),
                    rangeDeltaPercent: `${rangeDeltaPercent}%`,
                    rangeDeltaValue: rangeDelta // for sorting
                });
            });

            console.log(data);
            setTableData(data);
            setLoading(false);
        }
    }, [contractSummary, selectedMonth, selectedDateRange, selectedBaseDataMapKey]);

    const onMonthChange = (date, dateString) => {
        setSelectedMonth(dateString);
    };

    const onRangeChange = (dates, dateStrings) => {
        if (dates) {
            const [start, end] = dates;
            setSelectedDateRange({
                start: start.format(DATE_FORMAT),
                end: end.format(DATE_FORMAT)
            });
        } else {
            // Reset to current month when cleared
            const currentMonth = moment().startOf('month').format(DATE_FORMAT);
            setSelectedDateRange({
                start: currentMonth,
                end: currentMonth
            });
        }
    };

    const calculateValuesForRange = (serviceData, startDate, endDate, quantitiesOnly = false) => {
        let usage = 0;
        let contracted = 0;
        
        let current = moment(startDate);
        const end = moment(endDate);
        
        while (current.isSameOrBefore(end)) {
            const monthKey = current.format(DATE_FORMAT);
            if (quantitiesOnly) {
                usage += serviceData.usage[monthKey] || 0;
                contracted += serviceData.interval_quota[monthKey] || 0;
            } else {
                usage += serviceData.unit_price * (serviceData.usage[monthKey] || 0);
                contracted += serviceData.unit_price * (serviceData.interval_quota[monthKey] || 0);
            }
            current.add(1, 'month');
        }
        
        return { usage, contracted };
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => { searchInput = node; }}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput?.select(), 100);
            }
        }
    });

    const columns = [
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
            sorter: (a, b) => a.company.localeCompare(b.company),
            fixed: 'left',
            width: '16.5%',
            ...getColumnSearchProps('company')
        },
        {
            title: 'Monthly',
            onHeaderCell: () => ({
                className: 'color-block yellow',
            }),
            children: [
                {
                    title: 'Contracted Value',
                    dataIndex: 'monthlyContracted',
                    key: 'monthlyContracted',
                    sorter: (a, b) => parseFloat(a.monthlyContracted.replace(/[$,()]/g, '')) - parseFloat(b.monthlyContracted.replace(/[$,()]/g, '')),
                    onHeaderCell: () => ({
                        className: 'color-block yellow',
                    }),
                    className: 'color-block yellow',
                    width: '10.4375%',
                },
                {
                    title: 'Usage',
                    dataIndex: 'monthlyUsage',
                    key: 'monthlyUsage',
                    sorter: (a, b) => parseFloat(a.monthlyUsage.replace(/[$,()]/g, '')) - parseFloat(b.monthlyUsage.replace(/[$,()]/g, '')),
                    onHeaderCell: () => ({
                        className: 'color-block yellow',
                    }),
                    className: 'color-block yellow',
                    width: '10.4375%',
                },
                {
                    title: 'Remaining',
                    dataIndex: 'monthlyDelta',
                    key: 'monthlyDelta',
                    onHeaderCell: () => ({
                        className: 'color-block yellow',
                    }),
                    onCell: (record) => ({
                        className: record.monthlyDeltaValue === 0 ? 'color-block green' : record.monthlyDeltaValue < 0 ? 'color-block sharp-yellow' : 'color-block red',
                    }),
                    sorter: (a, b) => a.monthlyDeltaValue - b.monthlyDeltaValue,
                    width: '10.4375%',
                },
                {
                    title: 'Remaining %',
                    dataIndex: 'monthlyDeltaPercent',
                    key: 'monthlyDeltaPercent',
                    onHeaderCell: () => ({
                        className: 'color-block yellow',
                    }),
                    onCell: (record) => ({
                        className: record.monthlyDeltaValue === 0 ? 'color-block green' : record.monthlyDeltaValue < 0 ? 'color-block sharp-yellow' : 'color-block red',
                    }),
                    sorter: (a, b) => parseFloat(a.monthlyDeltaPercent) - parseFloat(b.monthlyDeltaPercent),
                    width: '10.4375%',
                },
            ],
        },
        {
            title: 'Date Range',
            onHeaderCell: () => ({
                className: 'color-block green',
            }),
            children: [
                {
                    title: 'Contracted Value',
                    dataIndex: 'rangeContracted',
                    key: 'rangeContracted',
                    sorter: (a, b) => parseFloat(a.rangeContracted.replace(/[$,()]/g, '')) - parseFloat(b.rangeContracted.replace(/[$,()]/g, '')),
                    onHeaderCell: () => ({
                        className: 'color-block green',
                    }),
                    className: 'color-block green',
                    width: '10.4375%',
                },
                {
                    title: 'Usage',
                    dataIndex: 'rangeUsage',
                    key: 'rangeUsage',
                    sorter: (a, b) => parseFloat(a.rangeUsage.replace(/[$,()]/g, '')) - parseFloat(b.rangeUsage.replace(/[$,()]/g, '')),
                    onHeaderCell: () => ({
                        className: 'color-block green',
                    }),
                    className: 'color-block green',
                    width: '10.4375%',
                },
                {
                    title: 'Remaining',
                    dataIndex: 'rangeDelta',
                    key: 'rangeDelta',
                    sorter: (a, b) => a.rangeDeltaValue - b.rangeDeltaValue,
                    onHeaderCell: () => ({
                        className: 'color-block green',
                    }),
                    onCell: (record) => ({
                        className: record.rangeDeltaValue === 0 ? 'color-block green' : record.rangeDeltaValue < 0 ? 'color-block sharp-yellow' : 'color-block red',
                    }),
                    width: '10.4375%',
                },
                {
                    title: 'Remaining %',
                    dataIndex: 'rangeDeltaPercent',
                    key: 'rangeDeltaPercent',
                    sorter: (a, b) => parseFloat(a.rangeDeltaPercent) - parseFloat(b.rangeDeltaPercent),
                    onHeaderCell: () => ({
                        className: 'color-block green',
                    }),
                    onCell: (record) => ({
                        className: record.rangeDeltaValue === 0 ? 'color-block green' : record.rangeDeltaValue < 0 ? 'color-block sharp-yellow' : 'color-block red',
                    }),
                    width: '10.4375%',
                },
            ],
        },
    ];

    const baseDataMapDropdown = (
        <Dropdown 
          overlay={
            <Menu selectedKeys={[selectedBaseDataMapKey]}>
              {BASE_DATA_MAP_KEYS.map((item) => (
                <Menu.Item onClick={() => setSelectedBaseDataMapKey(item)} key={item}>
                    {item}
                </Menu.Item>
              ))}
            </Menu>
          }
          trigger={['click']}>
            <Button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', marginRight: '10px' }}>
                <div style={{overflow: 'hidden'}}>
                    {selectedBaseDataMapKey}
                </div>
                <DownOutlined style={{ marginLeft: 8 }} />
            </Button>
        </Dropdown>
    );

    const exportToCSV = () => {
        let csvRows = [];

        // Helper function to escape CSV data
        const escapeCSV = (value) => {
            if (value === null || value === undefined) return '';
            const stringValue = value.toString();
            // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        };

        // Add headers
        csvRows.push([
            "Active Customers", 
            "Contract Value", 
            "Usage", 
            "Remaining", 
            "Remaining %", 
        ].join(','));

        tableData.map(row => {
            csvRows.push([
                escapeCSV(row.company),
                escapeCSV(row.monthlyContracted),
                escapeCSV(row.monthlyUsage),
                escapeCSV(row.monthlyDelta),
                escapeCSV(row.monthlyDeltaPercent),
            ].join(','));
        });

        const csvData = csvRows.join('\n');
        if (csvData) {
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
            saveAs(blob, `contract-summary.csv`);
        }
    };

    return (
        <AdminMainLayout selected={"9"}>
            {loading ? <DottedLoader /> : (
                <>
                    <Row className="mt-10 mb-4">
                        <Col span={4}>
                        <Button
                            className='product-btn dark-blue f-16 ml-16 br-4 pd-16'
                            onClick={exportToCSV}>
                            Export to CSV
                        </Button>
                        </Col>
                        <Col span={10} className='pd-10 color-block yellow'>
                            {baseDataMapDropdown}
                            <DatePicker 
                                picker="month" 
                                onChange={onMonthChange} 
                                format={DATE_FORMAT}
                                value={moment(selectedMonth, DATE_FORMAT)}
                                defaultValue={moment(selectedMonth, DATE_FORMAT)}
                                allowClear={false}
                                inputReadOnly={true}
                            />
                        </Col>
                        <Col span={10} className='pd-10 color-block green'>
                            <RangePicker
                                picker="month"
                                onChange={onRangeChange}
                                format={DATE_FORMAT}
                                allowClear={true}
                                defaultValue={[moment(selectedDateRange.start), moment(selectedDateRange.end)]}
                                value={selectedDateRange ? [moment(selectedDateRange.start), moment(selectedDateRange.end)] : null}
                            />
                        </Col>
                    </Row>
                    <Table 
                        columns={columns} 
                        dataSource={tableData}
                        pagination={false}
                        bordered
                        scroll={{ y: 'calc(100vh - 300px)' }}
                    />
                </>
            )}
        </AdminMainLayout>
    );
};

export default ContractSummary;
